import Model, { attr, hasMany } from '@ember-data/model';
import { cached, tracked } from '@glimmer/tracking';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  type IVrboMappingProgress,
  type VrboConnectionStatus,
  type VrboMarket,
  VRBO_CONNECTION_STATUSES,
} from 'uplisting-frontend/utils/vrbo';

export type ConnectionStatus =
  | 'active'
  | 'inactive'
  | 'onboarding'
  | 'pending_contract'
  | 'pending'
  | 'mapping'
  | 'syncing'
  | 'failed';

export type VrboOnboardingPath = 'net_new' | 'platform' | 'software_switch';

export interface IVrboConnectionData {
  advertiser_id?: string | null;
  market?: VrboMarket | null;
  sign_up_url?: string | null;
  email_address?: string | null;
  company_name?: string | null;
  onboarding_path?: VrboOnboardingPath | null;
  remote_onboarding_status?: string | null;
  master_brand?: string | null;
  currency?: string | null;
  connected_at?: string | null;
  synced_at?: string | null;
  last_onboarding_error?: string | null;
  mapping_progress?: IVrboMappingProgress | null;
  archive_external_ids?: string[];
  retry?: boolean;
}

export interface IChannelConnectionData extends IVrboConnectionData {
  code?: string;
  legal_entity_id?: string;
}

export type AccessLevel = 'full' | 'read_only';

export type ChannelType = 'airbnb' | 'booking_dot_com' | 'vrbo';

export default class ChannelConnectionModel extends Model {
  @attr('string') name!: string;
  @attr('string') type!: ChannelType;
  @attr('string') status!: ConnectionStatus | null;
  @attr('string') accessLevel!: AccessLevel;
  @attr('object') data!: IChannelConnectionData;

  @hasMany('channel-listing', { async: false, inverse: 'channelConnection' })
  channelListings!: ModelRegistry['channel-listing'][];

  @hasMany('channel-link', { async: false, inverse: 'channelConnection' })
  channelLinks!: ModelRegistry['channel-link'][];

  // handling state on the page, because we want to know which items are expanded and collapsed
  @tracked isCollapsed = true;

  @cached
  get isOnboarding(): boolean {
    return this.status === 'onboarding';
  }

  @cached
  get isVrbo(): boolean {
    return this.type === 'vrbo';
  }

  @cached
  get vrboStatus(): VrboConnectionStatus {
    return (
      VRBO_CONNECTION_STATUSES.find((status) => status === this.status) ??
      'active'
    );
  }

  @cached
  get isPending(): boolean {
    return this.isVrbo && this.vrboStatus === 'pending';
  }

  @cached
  get isMapping(): boolean {
    return this.isVrbo && this.vrboStatus === 'mapping';
  }

  @cached
  get isSyncing(): boolean {
    return this.isVrbo && this.vrboStatus === 'syncing';
  }

  @cached
  get isFailed(): boolean {
    return this.isVrbo && this.vrboStatus === 'failed';
  }

  @cached
  get isVrboEditable(): boolean {
    return this.isPending || (this.isVrbo && this.isOnboarding);
  }

  @cached
  get isAwaitingChannel(): boolean {
    if (this.isOnboarding || this.isPending || this.isSyncing) {
      return true;
    }

    return this.isMapping && (this.channelListings?.length ?? 0) === 0;
  }

  @cached
  get vrboData(): IVrboConnectionData {
    return this.data ?? {};
  }

  @cached
  get vrboDisplayName(): string {
    const { company_name: companyName, advertiser_id: advertiserId } =
      this.vrboData;

    return companyName || advertiserId || this.name;
  }

  @cached
  get isPendingContract(): boolean {
    return this.status === 'pending_contract';
  }

  @cached
  get isReadOnly(): boolean {
    return this.accessLevel === 'read_only';
  }

  // Names arrive as "Name [email]".
  @cached
  get displayName(): string {
    return (this.name ?? '').replace(/ \[[^\]]*\]$/, '');
  }

  @cached
  get email(): string | undefined {
    return /\[([^\]]*)\]$/.exec(this.name ?? '')?.[1];
  }

  @cached
  get originalId(): string {
    return this.id.split('_').pop() ?? this.id;
  }

  @cached
  get hasListingsToImport(): boolean {
    return (
      (this.channelLinks?.length ?? 0) < (this.channelListings?.length ?? 0)
    );
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'channel-connection': ChannelConnectionModel;
  }
}
